import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CurrencyPoundIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BellIcon,
  CogIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { 
  ukSchedulingAPI, 
  timeAttendanceAPI, 
  attendanceAPI, 
  leaveAPI, 
  payrollAPI, 
  reportsAPI,
  employeeAPI 
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const EnhancedDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    kpis: {},
    charts: {},
    alerts: [],
    quickActions: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTimeframe, setActiveTimeframe] = useState('week');

  const { user, isEmployee, isManager, isAdmin, isHRManager, isPayrollOfficer } = useAuth();
  const navigate = useNavigate();

  // Utility functions for generating dashboard data
  const generateAbsenceChart = (leaveData) => {
    return {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      datasets: [{
        label: 'Absences',
        data: [2, 5, 3, 8, 1],
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
      }]
    };
  };

  const generateCoverageChart = (schedulingData) => {
    return {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [{
        label: 'Coverage %',
        data: [95, 87, 92, 98],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
      }]
    };
  };

  const generateOvertimeAlerts = (attendanceData) => [
    { type: 'warning', message: '5 employees approaching overtime limit', severity: 'medium' }
  ];

  const generateCoverageAlerts = (schedulingData) => [
    { type: 'alert', message: '3 shifts require immediate coverage', severity: 'high' }
  ];

  const generateRecentActivity = (attendanceData, schedulingData) => [
    { action: 'Clock In', employee: 'John Smith', time: '09:00' },
    { action: 'Shift Published', shift: 'Evening Shift', time: '08:45' },
    { action: 'Leave Approved', employee: 'Jane Doe', time: '08:30' }
  ];

  const generateTeamCoverageChart = (teamData) => {
    return {
      labels: ['Morning', 'Afternoon', 'Evening', 'Night'],
      datasets: [{
        label: 'Team Coverage',
        data: [85, 92, 78, 65],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      }]
    };
  };

  const generateAttendanceTrendsChart = (attendanceData) => {
    return {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      datasets: [{
        label: 'Attendance Rate %',
        data: [94, 96, 93, 97, 95],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
      }]
    };
  };

  const generateManagerAlerts = (coverageData, overtimeData) => [
    { type: 'urgent', message: 'Weekend shifts need immediate coverage', count: 5 },
    { type: 'warning', message: 'Overtime budget at 85% capacity', count: 12 }
  ];

  useEffect(() => {
    fetchDashboardData();
  }, [activeTimeframe]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      if (isAdmin() || isHRManager()) {
        await fetchAdminDashboard();
      } else if (isManager()) {
        await fetchManagerDashboard();
      } else if (isPayrollOfficer()) {
        await fetchPayrollDashboard();
      } else if (isEmployee()) {
        await fetchEmployeeDashboard();
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminDashboard = async () => {
    const now = new Date();
    const startDate = new Date(now.setDate(now.getDate() - (activeTimeframe === 'week' ? 7 : 30))).toISOString();
    const endDate = new Date().toISOString();

    // Fetch KPI data
    const [
      employeesResponse,
      schedulingDashboard,
      attendanceDashboard,
      leaveRequests,
      payrollSummary,
      complianceReport
    ] = await Promise.all([
      employeeAPI.getAll({ active: true }),
      ukSchedulingAPI.getDashboard({ 
        company_id: user.companyId,
        date_from: startDate,
        date_to: endDate
      }),
      timeAttendanceAPI.getDashboard({
        company_id: user.companyId,
        date_from: startDate,
        date_to: endDate
      }),
      leaveAPI.getRequests({ status: 'pending' }),
      payrollAPI.getSummary({ period: activeTimeframe }),
      reportsAPI.getDashboard({ type: 'compliance' })
    ]);

    setDashboardData({
      kpis: {
        totalEmployees: employeesResponse.data.employees?.length || 0,
        shiftsScheduled: schedulingDashboard.data.dashboard?.total_shifts || 0,
        pendingLeaveRequests: leaveRequests.data.requests?.length || 0,
        overtimeHours: attendanceDashboard.data.dashboard?.overtime_hours || 0,
        labourCost: payrollSummary.data.summary?.total_cost || 0,
        complianceAlerts: complianceReport.data.dashboard?.violations?.length || 0
      },
      charts: {
        labourCostTrend: generateLabourCostChart(payrollSummary.data),
        overtimeTrends: generateOvertimeChart(attendanceDashboard.data),
        absenceTrends: generateAbsenceChart(leaveRequests.data),
        shiftCoverage: generateCoverageChart(schedulingDashboard.data)
      },
      alerts: [
        ...generateComplianceAlerts(complianceReport.data),
        ...generateOvertimeAlerts(attendanceDashboard.data),
        ...generateCoverageAlerts(schedulingDashboard.data)
      ],
      quickActions: [
        { 
          label: 'Add Employee', 
          action: () => navigate('/employees?action=create'),
          icon: UserGroupIcon,
          color: 'bg-blue-500 hover:bg-blue-600'
        },
        { 
          label: 'Create Shift Template', 
          action: () => navigate('/uk-scheduling?tab=templates&action=create'),
          icon: CalendarIcon,
          color: 'bg-green-500 hover:bg-green-600'
        },
        { 
          label: 'Run Payroll Preview', 
          action: () => navigate('/payroll?action=preview'),
          icon: CurrencyPoundIcon,
          color: 'bg-purple-500 hover:bg-purple-600'
        },
        { 
          label: 'Generate Compliance Report', 
          action: () => navigate('/reports?type=compliance'),
          icon: ShieldCheckIcon,
          color: 'bg-red-500 hover:bg-red-600'
        }
      ],
      recentActivity: generateRecentActivity(attendanceDashboard.data, schedulingDashboard.data)
    });
  };

  const fetchManagerDashboard = async () => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    const [
      teamShifts,
      coverageGaps,
      pendingSwaps,
      overtimeAlerts,
      teamAttendance
    ] = await Promise.all([
      ukSchedulingAPI.getShifts({
        manager_id: user.employeeId,
        date_from: startDate.toISOString(),
        date_to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      }),
      ukSchedulingAPI.getDashboard({ manager_view: true }),
      ukSchedulingAPI.getShifts({ status: 'swap_requested' }),
      timeAttendanceAPI.getDashboard({ manager_id: user.employeeId }),
      attendanceAPI.getTeamAttendance({ manager_id: user.employeeId })
    ]);

    setDashboardData({
      kpis: {
        upcomingShifts: teamShifts.data.shifts?.length || 0,
        coverageGaps: coverageGaps.data.dashboard?.coverage_gaps || 0,
        pendingSwaps: pendingSwaps.data.shifts?.length || 0,
        overtimeBreaches: overtimeAlerts.data.dashboard?.overtime_alerts || 0
      },
      charts: {
        teamCoverage: generateTeamCoverageChart(teamShifts.data),
        attendanceTrends: generateAttendanceTrendsChart(teamAttendance.data)
      },
      alerts: generateManagerAlerts(coverageGaps.data, overtimeAlerts.data),
      quickActions: [
        { 
          label: 'Assign Shift', 
          action: () => navigate('/uk-scheduling?action=assign'),
          icon: CalendarIcon,
          color: 'bg-blue-500 hover:bg-blue-600'
        },
        { 
          label: 'Approve Leave', 
          action: () => navigate('/leaves?filter=pending'),
          icon: CheckCircleIcon,
          color: 'bg-green-500 hover:bg-green-600'
        },
        { 
          label: 'Reassign Shifts', 
          action: () => navigate('/uk-scheduling?tab=reassign'),
          icon: ClockIcon,
          color: 'bg-orange-500 hover:bg-orange-600'
        }
      ]
    });
  };

  const fetchEmployeeDashboard = async () => {
    const [
      myShifts,
      leaveBalance,
      pendingRequests,
      attendanceStatus,
      notifications
    ] = await Promise.all([
      ukSchedulingAPI.getShifts({
        employee_id: user.employeeId,
        date_from: new Date().toISOString(),
        date_to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      }),
      leaveAPI.getBalance({ employee_id: user.employeeId }),
      leaveAPI.getRequests({ employee_id: user.employeeId, status: 'pending' }),
      timeAttendanceAPI.getStatus(user.employeeId),
      // Mock notifications - implement notification service
      Promise.resolve({ data: { notifications: [] } })
    ]);

    setDashboardData({
      kpis: {
        nextShifts: myShifts.data.shifts?.length || 0,
        leaveBalance: leaveBalance.data.balance?.remaining || 0,
        pendingRequests: pendingRequests.data.requests?.length || 0,
        currentStatus: attendanceStatus.data.employee_status?.status || 'off_duty'
      },
      myShifts: myShifts.data.shifts || [],
      quickActions: [
        { 
          label: 'Request Leave', 
          action: () => navigate('/leaves?action=request'),
          icon: CalendarIcon,
          color: 'bg-blue-500 hover:bg-blue-600'
        },
        { 
          label: 'Request Shift Swap', 
          action: () => navigate('/uk-scheduling?action=swap'),
          icon: ClockIcon,
          color: 'bg-green-500 hover:bg-green-600'
        }
      ]
    });
  };

  const fetchPayrollDashboard = async () => {
    const [
      payrollStatus,
      timesheetsPending,
      exceptions,
      payrollRuns
    ] = await Promise.all([
      payrollAPI.getSummary(),
      timeAttendanceAPI.getPendingTimesheets(),
      timeAttendanceAPI.getDashboard({ exceptions: true }),
      payrollAPI.getAll({ limit: 5 })
    ]);

    setDashboardData({
      kpis: {
        currentPeriodStatus: payrollStatus.data.summary?.status || 'open',
        approvedHours: payrollStatus.data.summary?.approved_hours || 0,
        pendingTimesheets: timesheetsPending.data.pending_timesheets?.length || 0,
        exceptions: exceptions.data.dashboard?.exceptions || 0
      },
      quickActions: [
        { 
          label: 'Generate Payroll Run', 
          action: () => navigate('/payroll?action=generate'),
          icon: CurrencyPoundIcon,
          color: 'bg-green-500 hover:bg-green-600'
        }
      ]
    });
  };

  // Chart generation functions
  const generateLabourCostChart = (data) => ({
    labels: data?.cost_trends?.labels || [],
    datasets: [{
      label: 'Labour Cost (£)',
      data: data?.cost_trends?.values || [],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.4
    }]
  });

  const generateOvertimeChart = (data) => ({
    labels: data?.overtime_trends?.labels || [],
    datasets: [{
      label: 'Overtime Hours',
      data: data?.overtime_trends?.values || [],
      backgroundColor: 'rgba(239, 68, 68, 0.8)'
    }]
  });

  const generateComplianceAlerts = (data) => {
    if (!data?.violations) return [];
    return data.violations.map(violation => ({
      type: violation.severity === 'critical' ? 'error' : 'warning',
      message: violation.description,
      action: () => navigate('/uk-compliance-dashboard')
    }));
  };

  // Component render functions
  const renderKPICard = (title, value, icon, trend, color = 'blue') => (
    <div 
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p data-testid={`kpi-value-${title.toLowerCase().replace(/\s+/g, '-')}`} className={`text-2xl font-bold text-${color}-600`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {trend && (
            <div className={`flex items-center mt-1 text-sm ${
              trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'
            }`}>
              {trend > 0 ? <ArrowTrendingUpIcon className="w-4 h-4 mr-1" /> : 
               trend < 0 ? <ArrowTrendingDownIcon className="w-4 h-4 mr-1" /> : null}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className={`p-3 bg-${color}-100 rounded-full`}>
          {React.createElement(icon, { className: `w-6 h-6 text-${color}-600` })}
        </div>
      </div>
    </div>
  );

  const renderChart = (title, chartData, type = 'line') => (
    <div data-testid={`chart-${title.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="h-64">
        {type === 'line' && <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />}
        {type === 'bar' && <Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />}
        {type === 'doughnut' && <Doughnut data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />}
      </div>
    </div>
  );

  const renderAlert = (alert, index) => (
    <div 
      key={index}
      data-testid={`alert-${index}`}
      className={`p-4 rounded-lg border-l-4 ${
        alert.type === 'error' 
          ? 'bg-red-50 border-red-400' 
          : alert.type === 'warning'
          ? 'bg-yellow-50 border-yellow-400'
          : 'bg-blue-50 border-blue-400'
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="flex">
          <ExclamationTriangleIcon className={`w-5 h-5 mr-2 ${
            alert.type === 'error' ? 'text-red-400' : 
            alert.type === 'warning' ? 'text-yellow-400' : 'text-blue-400'
          }`} />
          <p className={`text-sm ${
            alert.type === 'error' ? 'text-red-800' : 
            alert.type === 'warning' ? 'text-yellow-800' : 'text-blue-800'
          }`}>
            {alert.message}
          </p>
        </div>
        {alert.action && (
          <button
            onClick={alert.action}
            className={`text-sm font-medium ${
              alert.type === 'error' ? 'text-red-600 hover:text-red-700' : 
              alert.type === 'warning' ? 'text-yellow-600 hover:text-yellow-700' : 'text-blue-600 hover:text-blue-700'
            }`}
          >
            View Details →
          </button>
        )}
      </div>
    </div>
  );

  const renderQuickAction = (action, index) => (
    <button
      key={index}
      data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={action.action}
      className={`${action.color} text-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-left`}
    >
      <div className="flex items-center">
        <action.icon className="w-6 h-6 mr-3" />
        <span className="font-medium">{action.label}</span>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="dashboard-error" className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Dashboard Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="enhanced-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 data-testid="dashboard-title" className="text-3xl font-bold text-gray-900">
            {isAdmin() || isHRManager() ? 'Admin Dashboard' :
             isManager() ? 'Manager Dashboard' :
             isPayrollOfficer() ? 'Payroll Dashboard' : 'My Dashboard'}
          </h1>
          <p className="text-gray-600">Welcome back, {user.name || user.email}</p>
        </div>
        
        {/* Timeframe selector */}
        <div data-testid="timeframe-selector" className="flex bg-gray-100 rounded-lg p-1">
          {['week', 'month', 'quarter'].map(period => (
            <button
              key={period}
              onClick={() => setActiveTimeframe(period)}
              className={`px-4 py-2 rounded-md capitalize transition-colors ${
                activeTimeframe === period 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div data-testid="kpi-section" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isAdmin() || isHRManager() ? (
          <>
            {renderKPICard('Total Employees', dashboardData.kpis.totalEmployees, UserGroupIcon, null, 'blue')}
            {renderKPICard('Shifts Scheduled', dashboardData.kpis.shiftsScheduled, CalendarIcon, null, 'green')}
            {renderKPICard('Pending Leave Requests', dashboardData.kpis.pendingLeaveRequests, ClockIcon, null, 'yellow')}
            {renderKPICard('Compliance Alerts', dashboardData.kpis.complianceAlerts, ShieldCheckIcon, null, 'red')}
          </>
        ) : isManager() ? (
          <>
            {renderKPICard('Upcoming Shifts', dashboardData.kpis.upcomingShifts, CalendarIcon, null, 'blue')}
            {renderKPICard('Coverage Gaps', dashboardData.kpis.coverageGaps, ExclamationTriangleIcon, null, 'red')}
            {renderKPICard('Pending Swaps', dashboardData.kpis.pendingSwaps, ClockIcon, null, 'yellow')}
            {renderKPICard('Overtime Breaches', dashboardData.kpis.overtimeBreaches, ArrowTrendingUpIcon, null, 'orange')}
          </>
        ) : isEmployee() ? (
          <>
            {renderKPICard('Next 7 Days Shifts', dashboardData.kpis.nextShifts, CalendarIcon, null, 'blue')}
            {renderKPICard('Leave Balance', dashboardData.kpis.leaveBalance, ClockIcon, null, 'green')}
            {renderKPICard('Pending Requests', dashboardData.kpis.pendingRequests, DocumentTextIcon, null, 'yellow')}
            {renderKPICard('Current Status', dashboardData.kpis.currentStatus, CheckCircleIcon, null, 'blue')}
          </>
        ) : (
          <>
            {renderKPICard('Pay Period Status', dashboardData.kpis.currentPeriodStatus, CurrencyPoundIcon, null, 'green')}
            {renderKPICard('Approved Hours', dashboardData.kpis.approvedHours, ClockIcon, null, 'blue')}
            {renderKPICard('Pending Timesheets', dashboardData.kpis.pendingTimesheets, DocumentTextIcon, null, 'yellow')}
            {renderKPICard('Exceptions', dashboardData.kpis.exceptions, ExclamationTriangleIcon, null, 'red')}
          </>
        )}
      </div>

      {/* Charts Section */}
      {dashboardData.charts && Object.keys(dashboardData.charts).length > 0 && (
        <div data-testid="charts-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {dashboardData.charts.labourCostTrend && renderChart('Labour Cost Trends', dashboardData.charts.labourCostTrend, 'line')}
          {dashboardData.charts.overtimeTrends && renderChart('Overtime Trends', dashboardData.charts.overtimeTrends, 'bar')}
          {dashboardData.charts.absenceTrends && renderChart('Absence Trends', dashboardData.charts.absenceTrends, 'line')}
          {dashboardData.charts.shiftCoverage && renderChart('Shift Coverage', dashboardData.charts.shiftCoverage, 'doughnut')}
        </div>
      )}

      {/* Alerts Section */}
      {dashboardData.alerts && dashboardData.alerts.length > 0 && (
        <div data-testid="alerts-section">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Alerts & Notifications</h2>
          <div className="space-y-4">
            {dashboardData.alerts.map(renderAlert)}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div data-testid="quick-actions-section">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {dashboardData.quickActions.map(renderQuickAction)}
        </div>
      </div>

      {/* Employee Specific - My Shifts */}
      {isEmployee() && dashboardData.myShifts && dashboardData.myShifts.length > 0 && (
        <div data-testid="my-shifts-section" className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">My Next 7 Days</h2>
          <div className="space-y-3">
            {dashboardData.myShifts.map((shift, index) => (
              <div key={shift.shift_id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    {new Date(shift.start_ts).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(shift.start_ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - 
                    {new Date(shift.end_ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{shift.site_name}</p>
                  <p className="text-sm text-gray-600">{shift.role_name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDashboard;